/**
 * Notification Management Plugin
 * Handles browser notifications, sounds, and visual alerts for WebSocket events
 */

export class NotificationManager {
  constructor(config = {}) {
    this.config = {
      enableNotifications: true,
      enableSounds: true,
      enableVisualAlerts: true,
      notificationPermission: 'default',
      soundVolume: 0.3,
      soundDuration: 0.2,
      maxNotifications: 10,
      ...config
    };

    // Notification queue
    this.notificationQueue = [];
    this.activeNotifications = new Map();
    
    // Audio context for sounds
    this.audioContext = null;
    
    // Visual alert element
    this.alertContainer = null;

    // Initialize
    this._initialize();
  }

  /**
   * Initialize notification manager
   */
  async _initialize() {
    // Request notification permission
    await this.requestNotificationPermission();
    
    // Initialize audio context
    this._initializeAudioContext();
    
    // Create alert container
    this._createAlertContainer();
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission() {
    if (!('Notification' in window)) {
      console.warn('⚠️ Browser notifications not supported');
      this.config.enableNotifications = false;
      return false;
    }

    if (Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        this.config.notificationPermission = permission;
        console.log(`🔔 Notification permission: ${permission}`);
        return permission === 'granted';
      } catch (error) {
        console.error('❌ Failed to request notification permission:', error);
        return false;
      }
    }

    this.config.notificationPermission = Notification.permission;
    return Notification.permission === 'granted';
  }

  /**
   * Show notification for WebSocket message
   */
  showNotification(message, options = {}) {
    if (!this.config.enableNotifications) return;

    const notificationData = {
      id: message.id || `notif-${Date.now()}`,
      title: this._getNotificationTitle(message, options),
      body: this._getNotificationBody(message, options),
      icon: options.icon || '/favicon.ico',
      tag: message.type || 'websocket',
      timestamp: Date.now(),
      urgent: message.payload?.urgent || options.urgent || false,
      ...options
    };

    // Add to queue
    this.notificationQueue.push(notificationData);
    
    // Process notification
    this._processNotification(notificationData);

    // Show visual alert if enabled
    if (this.config.enableVisualAlerts) {
      this.showVisualAlert(notificationData);
    }

    // Play sound for urgent messages
    if (notificationData.urgent && this.config.enableSounds) {
      this.playNotificationSound('urgent');
    } else if (this.config.enableSounds) {
      this.playNotificationSound('normal');
    }

    return notificationData;
  }

  /**
   * Process notification
   */
  _processNotification(notificationData) {
    if (this.config.notificationPermission !== 'granted') {
      console.warn('⚠️ Notification permission not granted');
      return;
    }

    try {
      const notification = new Notification(notificationData.title, {
        body: notificationData.body,
        icon: notificationData.icon,
        tag: notificationData.tag,
        timestamp: notificationData.timestamp,
        requireInteraction: notificationData.urgent,
        silent: !this.config.enableSounds
      });

      // Store active notification
      this.activeNotifications.set(notificationData.id, notification);

      // Auto-close after delay (unless urgent)
      if (!notificationData.urgent) {
        setTimeout(() => {
          notification.close();
          this.activeNotifications.delete(notificationData.id);
        }, 5000);
      }

      // Handle click events
      notification.onclick = () => {
        window.focus();
        notification.close();
        this.activeNotifications.delete(notificationData.id);
        
        // Custom click handler
        if (notificationData.onClick) {
          notificationData.onClick(notificationData);
        }
      };

      console.log('🔔 Notification shown:', notificationData.title);

    } catch (error) {
      console.error('❌ Failed to show notification:', error);
    }

    // Keep queue size manageable
    if (this.notificationQueue.length > this.config.maxNotifications) {
      this.notificationQueue = this.notificationQueue.slice(-this.config.maxNotifications);
    }
  }

  /**
   * Show visual alert in the page
   */
  showVisualAlert(notificationData) {
    if (!this.alertContainer) return;

    const alert = document.createElement('div');
    alert.className = `socket-alert ${notificationData.urgent ? 'urgent' : 'normal'}`;
    alert.innerHTML = `
      <div class="socket-alert-content">
        <div class="socket-alert-title">${notificationData.title}</div>
        <div class="socket-alert-body">${notificationData.body}</div>
        <button class="socket-alert-close">&times;</button>
      </div>
    `;

    // Add styles
    this._styleAlert(alert, notificationData.urgent);

    // Handle close button
    const closeBtn = alert.querySelector('.socket-alert-close');
    closeBtn.onclick = () => {
      alert.remove();
    };

    // Auto-remove after delay
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove();
      }
    }, notificationData.urgent ? 10000 : 5000);

    this.alertContainer.appendChild(alert);

    // Animate in
    setTimeout(() => {
      alert.style.opacity = '1';
      alert.style.transform = 'translateX(0)';
    }, 10);
  }

  /**
   * Play notification sound
   */
  playNotificationSound(type = 'normal') {
    if (!this.config.enableSounds || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Configure sound based on type
      if (type === 'urgent') {
        // Higher pitch, longer duration for urgent
        oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime);
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(1000, this.audioContext.currentTime + 0.2);
        gainNode.gain.setValueAtTime(this.config.soundVolume, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.4);
        
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + 0.4);
      } else {
        // Normal notification sound
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        oscillator.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(this.config.soundVolume, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + this.config.soundDuration);
        
        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + this.config.soundDuration);
      }

    } catch (error) {
      console.warn('⚠️ Could not play notification sound:', error);
    }
  }

  /**
   * Show connection status notification
   */
  showConnectionStatus(status, data = {}) {
    const statusMessages = {
      connecting: { title: '🔌 Connecting...', body: 'Establishing WebSocket connection' },
      connected: { title: '✅ Connected', body: `Connected successfully${data.connectTime ? ` in ${data.connectTime}ms` : ''}` },
      disconnected: { title: '🔌 Disconnected', body: data.reason || 'Connection closed' },
      error: { title: '❌ Connection Error', body: data.error?.message || 'Connection failed' }
    };

    const message = statusMessages[status];
    if (!message) return;

    return this.showNotification({
      type: 'connection_status',
      payload: { status, ...data }
    }, {
      title: message.title,
      body: message.body,
      urgent: status === 'error'
    });
  }

  /**
   * Show message notification
   */
  showMessageNotification(message, options = {}) {
    return this.showNotification(message, {
      title: `📥 ${message.type || 'Message'}`,
      body: this._getMessagePreview(message),
      urgent: message.payload?.urgent || false,
      ...options
    });
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications() {
    // Close active browser notifications
    this.activeNotifications.forEach(notification => {
      notification.close();
    });
    this.activeNotifications.clear();

    // Clear visual alerts
    if (this.alertContainer) {
      this.alertContainer.innerHTML = '';
    }

    // Clear queue
    this.notificationQueue = [];

    console.log('🔔 All notifications cleared');
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    
    // Re-initialize if necessary
    if (newConfig.enableSounds !== undefined && !this.audioContext) {
      this._initializeAudioContext();
    }
  }

  /**
   * Get notification title
   */
  _getNotificationTitle(message, options) {
    if (options.title) return options.title;
    
    const typeMap = {
      'ping': '🏓 Ping',
      'pong': '🏓 Pong',
      'notification': '🔔 Notification',
      'error': '❌ Error',
      'health': '💚 Health Check',
      'handshake': '🤝 Handshake',
      'message': '📨 Message'
    };

    return typeMap[message.type] || '📨 WebSocket Message';
  }

  /**
   * Get notification body
   */
  _getNotificationBody(message, options) {
    if (options.body) return options.body;
    
    if (message.payload?.message) {
      return message.payload.message;
    } else if (message.content) {
      return message.content.length > 100 ? message.content.substring(0, 97) + '...' : message.content;
    } else if (message.payload) {
      const preview = JSON.stringify(message.payload);
      return preview.length > 100 ? preview.substring(0, 97) + '...' : preview;
    } else {
      return 'New WebSocket message received';
    }
  }

  /**
   * Get message preview for notifications
   */
  _getMessagePreview(message) {
    const content = message.content || message.payload?.message || JSON.stringify(message.payload);
    return content.length > 50 ? content.substring(0, 47) + '...' : content;
  }

  /**
   * Initialize audio context
   */
  _initializeAudioContext() {
    if (!this.config.enableSounds) return;

    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.warn('⚠️ Could not initialize audio context:', error);
      this.config.enableSounds = false;
    }
  }

  /**
   * Create alert container
   */
  _createAlertContainer() {
    if (!this.config.enableVisualAlerts) return;

    this.alertContainer = document.createElement('div');
    this.alertContainer.className = 'socket-alert-container';
    this.alertContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 10000;
      pointer-events: none;
    `;

    document.body.appendChild(this.alertContainer);
  }

  /**
   * Style alert element
   */
  _styleAlert(alert, urgent = false) {
    const baseStyles = `
      margin-bottom: 10px;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      opacity: 0;
      transform: translateX(300px);
      transition: all 0.3s ease;
      pointer-events: auto;
      max-width: 300px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    const urgentStyles = urgent ? `
      background: linear-gradient(135deg, #ef4444, #dc2626);
      color: white;
      border-left: 4px solid #991b1b;
    ` : `
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      color: white;
      border-left: 4px solid #1d4ed8;
    `;

    alert.style.cssText = baseStyles + urgentStyles;

    // Style content
    const content = alert.querySelector('.socket-alert-content');
    if (content) {
      content.style.cssText = `
        position: relative;
      `;
    }

    // Style title
    const title = alert.querySelector('.socket-alert-title');
    if (title) {
      title.style.cssText = `
        font-weight: 600;
        font-size: 14px;
        margin-bottom: 4px;
      `;
    }

    // Style body
    const body = alert.querySelector('.socket-alert-body');
    if (body) {
      body.style.cssText = `
        font-size: 12px;
        opacity: 0.9;
        line-height: 1.4;
      `;
    }

    // Style close button
    const closeBtn = alert.querySelector('.socket-alert-close');
    if (closeBtn) {
      closeBtn.style.cssText = `
        position: absolute;
        top: 8px;
        right: 8px;
        background: none;
        border: none;
        color: inherit;
        font-size: 18px;
        cursor: pointer;
        opacity: 0.7;
        line-height: 1;
        padding: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      closeBtn.onmouseenter = () => {
        closeBtn.style.opacity = '1';
      };

      closeBtn.onmouseleave = () => {
        closeBtn.style.opacity = '0.7';
      };
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    this.clearAllNotifications();
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.alertContainer && this.alertContainer.parentNode) {
      this.alertContainer.parentNode.removeChild(this.alertContainer);
      this.alertContainer = null;
    }
  }
}

// Export singleton instance
export const notificationManager = new NotificationManager();

// Export factory function
export function createNotificationManager(config) {
  return new NotificationManager(config);
}
